import { Err, ErrorCode } from "./error";
import { Feature, Role } from "./model";
import { unique } from "./utils";
export const PERMISSIONS = {
    key: "",
    title: "Permissions",
    children: [
        {
            key: "manage.time",
            feature: Feature.Time,
            requiresRole: Role.Manager,
            title: "Zeiterfassung",
            grid: true,
            icon: "icon.feature.time-tracking",
            description: "Der Mitarbeiter hat Zugriff auf den Bereich Zeiterfassung " +
                "und kann dort erfasste Arbeitszeiten einsehen. Desweiteren kann er " +
                "das Zeiterfassungsystem (Pentatime) auf Endgeräten einrichten und verwalten.",
            children: [
                {
                    title: "Einsehen & Bearbeiten",
                    key: "manage.time",
                    readonly: true,
                    requires: ["staff.roster"],
                    icon: "icon.action.edit",
                },
                {
                    title: "Protokoll Einsehen",
                    key: "manage.time.events",
                    requires: ["manage.time"],
                    icon: "icon.object.time-log",
                },
            ],
        },
        {
            key: "manage.roster",
            feature: Feature.Roster,
            requiresRole: Role.Manager,
            title: "Dienstplan",
            description: "Der Mitarbeiter kann für bestimmte Abteilungen Dienstpläne schreiben und veröffentlichen.",
            grid: true,
            icon: "icon.feature.roster",
            children: [
                {
                    title: "Einsehen & Bearbeiten",
                    key: "manage.roster",
                    icon: "icon.action.edit",
                    readonly: true,
                    requires: [`manage.roster`],
                },
                {
                    title: "Veröffentlichen",
                    key: "manage.roster.publish",
                    requires: ["manage.roster"],
                    icon: "icon.action.publish",
                },
                {
                    title: "Kosten Einsehen",
                    key: "manage.roster.costs",
                    requires: ["manage.roster"],
                    icon: "icon.object.currency",
                },
                {
                    title: "Notizen Verwalten",
                    key: "manage.roster.notes",
                    requires: ["manage.roster"],
                    icon: "icon.object.note",
                },
            ],
        },
        {
            key: "manage.employees",
            feature: Feature.Employees,
            requiresRole: Role.Manager,
            title: "Mitarbeiter",
            description: "Der Mitarbeiter hat Zugriff auf den Bereich Mitarbeiter und kann dort Stammdaten, Arbeitsbereiche und Berechtigungen einsehen und bearbeiten.",
            grid: true,
            icon: "icon.feature.employees",
            children: [
                {
                    key: "manage.employees.time",
                    title: "Arbeitszeiten",
                    icon: "icon.object.working-hours",
                },
                {
                    key: "manage.employees.ledgers_time",
                    title: "Arbeitszeitkonten",
                    icon: "icon.object.time-balance",
                },
                {
                    key: "manage.employees.ledgers_vacation",
                    title: "Urlaubskonten",
                    icon: "icon.object.vacation-balance",
                },
                {
                    key: "manage.employees.ledgers_bonuses",
                    title: "Zuschlagskonten",
                    icon: "icon.object.bonus",
                },
                {
                    key: "manage.employees.absences",
                    title: "Abwesenheiten",
                    icon: "icon.object.absence",
                },
                {
                    key: "manage.employees.availabilities",
                    title: "Verfügbarkeiten",
                    icon: "icon.planning.availability",
                },
                {
                    key: "manage.employees.documents",
                    title: "Digitale Personalakte",
                    icon: "icon.file.collection",
                },
                {
                    key: "manage.employees.advances",
                    title: "Vorschüsse",
                    icon: "icon.object.pay-advance",
                },
                {
                    key: "manage.employees.special_payments",
                    title: "Einmalzahlungen",
                    icon: "icon.object.currency",
                    featureFlag: "specialPayments",
                },
                {
                    key: "manage.employees.core_data",
                    title: "Stammdaten",
                    icon: "icon.object.master-data",
                },
                {
                    key: "manage.employees.positions",
                    title: "Positionen",
                    icon: "icon.object.position",
                },
                {
                    key: "manage.employees.permissions",
                    title: "Zugänge & Rechte",
                    icon: "icon.object.permissions",
                },
                {
                    key: "manage.employees.contract",
                    title: "Verträge",
                    icon: "icon.object.contract",
                },
                {
                    key: "manage.employees.create",
                    title: "Mitarbeiter Anlegen",
                    icon: "icon.action.add-employee",
                },
                {
                    key: "manage.employees.delete",
                    title: "Mitarbeiter Löschen",
                    icon: "icon.action.delete-employee",
                },
                {
                    key: "manage.employees.payroll",
                    title: "Lohnzusammenfassung",
                    icon: "icon.object.total",
                },
                {
                    key: "manage.employees.messages",
                    title: "Nachrichten Versenden",
                    icon: "icon.communication.message",
                },
            ],
        },
        {
            key: "manage.recruiting",
            requiresRole: Role.Manager,
            title: "Recruiting",
            icon: "icon.feature.recruiting",
            description: "Der Mitarbeiter hat Zugriff auf den Bereich Recruiting und kann dort " +
                "Stellenausschreibungen bearbeiten und Bewerbungen einsehen und beantworten.",
        },
        {
            key: "manage.revenues",
            feature: Feature.Revenues,
            requiresRole: Role.Manager,
            title: "Umsätze",
            icon: "icon.feature.revenues",
            description: "Der Mitarbeiter hat Zugriff auf den Bereich Umsätze und kann dort erfasste Umsätze einsehen " +
                "und bearbeiten. Zusätzlich kann er Umsatzgruppen und Erlöszuordnungen definieren.",
            grid: true,
            children: [
                { icon: "icon.object.revenue-overview", key: `manage.revenues.all`, title: "Alle Umsätze" },
                { icon: "icon.object.daily-closing", key: `manage.revenues.daily`, title: "Tagesabrechnung" },
                { icon: "icon.object.cashbook", key: `manage.revenues.cashbook`, title: "Kassenbuch" },
                { icon: "icon.object.invoice", key: `manage.revenues.debtors`, title: "Debitoren" },
                { icon: "icon.object.revenue-group", key: `manage.revenues.groups`, title: "Umsatzgruppen" },
            ],
        },
        {
            key: "manage.planning",
            feature: Feature.Planning,
            requiresRole: Role.Manager,
            title: "Planung",
            description: "Der Mitarbeiter hat Zugriff auf den Bereich Planung und kann dort " +
                "Planumsätze und Stundenvorgaben definieren.",
            grid: true,
            icon: "icon.feature.planning",
            children: [
                { key: `manage.planning.revenue`, title: "Umsätze", icon: "icon.feature.revenues" },
                { key: `manage.planning.roster`, title: "Stundenvorgaben", icon: "icon.planning.staffing-target" },
            ],
        },
        {
            key: "manage.reports",
            requiresRole: Role.Manager,
            title: "Berichte",
            description: "Der Mitarbeiter hat Zugriff auf den Bereich Berichte und kann dort " +
                "verschiedene Kennzahlen und Analysen einsehen.",
            grid: true,
            icon: "icon.feature.reports",
            children: [
                { key: `manage.reports.savings`, title: "Ersparnis", icon: "icon.object.savings" },
                { key: `manage.reports.costs`, title: "Personalkosten", icon: "icon.object.labour-cost" },
                { key: `manage.reports.time`, title: "Arbeitszeit", icon: "icon.object.working-hours" },
                { key: `manage.reports.revenue`, title: "Umsätze", icon: "icon.object.total" },
            ],
        },
        {
            key: "manage.settings",
            requiresRole: Role.Manager,
            title: "Einstellungen",
            description: "Der Mitarbeiter kann Änderungen an den Grundeinstellungen vornehmen.",
            grid: true,
            icon: "icon.feature.settings",
            children: [
                {
                    key: "manage.settings.company",
                    title: "Unternehmen",
                    icon: "icon.object.company",
                },
                {
                    key: "manage.settings.venues",
                    title: "Arbeitsbereiche",
                    icon: "icon.object.work-area",
                },
                {
                    key: "manage.settings.time",
                    title: "Zeiterfassung",
                    icon: "icon.feature.time-tracking",
                },
                {
                    key: "manage.settings.core_data",
                    title: "Stammdaten",
                    icon: "icon.object.master-data",
                },
                {
                    key: "manage.settings.documents",
                    title: "Dokumente",
                    icon: "icon.file.collection",
                },
                {
                    key: "manage.settings.bonuses",
                    title: "Zuschläge",
                    icon: "icon.object.bonus",
                },
                {
                    key: "manage.settings.benefits",
                    title: "Sonderleistungen",
                    icon: "icon.object.benefit",
                },
                {
                    key: "manage.settings.accounting",
                    title: "Buchhaltung",
                    icon: "icon.object.accounting",
                },
                {
                    key: "manage.settings.billing",
                    title: "Rechnungsstellung",
                    icon: "icon.object.invoice",
                },
            ],
        },
        {
            key: "manage.issues",
            requiresRole: Role.Manager,
            title: "Probleme",
            icon: "icon.feature.issues",
            description: "Der Mitarbeiter hat Zugriff auf den Bereich Probleme und kann dort " +
                "Arbeitszeit- und Vertragsbezogene Probleme einsehen, beheben und verwerfen.",
        },
        {
            key: "manage.exports",
            requiresRole: Role.Manager,
            title: "Datenexport",
            description: 'Der Mitarbeiter hat Zugriff auf den Bereich "Datenexport" und kann dort ' +
                "verschiedene Bewegungs- und Stammdaten exportieren.",
            grid: true,
            icon: "icon.feature.exports",
            children: [
                {
                    key: "manage.exports.payroll",
                    title: "Lohnbuchhaltung",
                    icon: "icon.object.labour-cost",
                },
                {
                    key: "manage.exports.time",
                    title: "Arbeitszeiten",
                    icon: "icon.object.time-history",
                },
                {
                    key: "manage.exports.roster",
                    title: "Dienstplan",
                    icon: "icon.feature.roster",
                },
                {
                    key: "manage.exports.ledgers_time",
                    title: "Arbeitszeitkonten",
                    icon: "icon.object.time-balance",
                },
                {
                    key: "manage.exports.ledgers_vacation",
                    title: "Urlaubskonten",
                    icon: "icon.object.vacation-balance",
                },
                {
                    key: "manage.exports.ledgers_bonuses",
                    title: "Zuschlagskonten",
                    icon: "icon.object.bonus",
                },
                {
                    key: "manage.exports.employee_data",
                    title: "Stammdaten",
                    icon: "icon.object.master-data",
                },
                {
                    key: "manage.exports.cashbook",
                    title: "Kassenbuch",
                    icon: "icon.object.cashbook",
                },
            ],
        },
        {
            key: "staff.roster",
            title: "Dienstplan",
            description: "Der Mitarbeiter kann seinen persönlichen Dienstplan einsehen",
            grid: true,
            icon: "icon.feature.roster",
            children: [
                {
                    title: "Einsehen",
                    key: "staff.roster",
                    readonly: true,
                    requires: ["staff.roster"],
                    icon: "icon.action.view",
                },
                {
                    title: "Schichten Übernehmen",
                    key: "staff.roster.take",
                    requires: ["staff.roster"],
                    icon: "icon.action.claim-shift",
                },
                {
                    title: "Zum Tausch Freigeben",
                    key: "staff.roster.offer",
                    requires: ["staff.roster"],
                    icon: "icon.action.offer-shift",
                },
            ],
        },
        {
            key: "staff.absences",
            title: "Abwesenheiten",
            description: "Der Mitarbeiter kann seine eigenen Abwesenheitszeiträume einsehen, Urlaubsanträge stellen und Krankmeldungen einreichen.",
            grid: true,
            icon: "icon.object.absence",
            children: [
                {
                    title: "Einsehen",
                    key: "staff.absences",
                    readonly: true,
                    requires: ["staff.absences"],
                    icon: "icon.action.view",
                },
                {
                    title: "Urlaubsanträge",
                    key: "staff.absences.request_vacation",
                    requires: ["staff.absences"],
                    icon: "icon.planning.vacation-request",
                },
                {
                    title: "Krankmeldungen",
                    key: "staff.absences.request_sick",
                    requires: ["staff.absences"],
                    icon: "icon.action.report-sick",
                },
            ],
        },
        {
            key: "staff.availabilities",
            title: "Verfügbarkeiten",
            description: "Der Mitarbeiter kann über die Mitarbeiter-App Verfügbarkeiten eintragen.",
            icon: "icon.planning.availability",
        },
        {
            key: "staff.time",
            title: "Arbeitszeiten",
            description: "Der Mitarbeiter kann seine eigenen Arbeitszeiten einsehen.",
            icon: "icon.object.working-hours",
        },
        {
            key: "staff.ledgers",
            title: "Konten",
            description: "Der Mitarbeiter kann seine eigenen Konten einsehen.",
            icon: "icon.object.time-balance",
        },
        {
            key: "staff.tracking",
            title: "Stempeluhr",
            description: "Der Mitarbeiter kann über die Mitarbeiter-App Zeiten stempeln.",
            icon: "icon.feature.time-tracking",
        },
        {
            key: "staff.documents",
            title: "Dokumente",
            description: "Der Mitarbeiter kann Dokumente aus der Digitalen Personalakte einsehen und eigene Dokumente hinzufügen",
            grid: true,
            icon: "icon.file.collection",
            children: [
                {
                    title: "Einsehen",
                    key: "staff.documents",
                    readonly: true,
                    requires: ["staff.documents"],
                    icon: "icon.action.view",
                },
                {
                    title: "Hinzufügen",
                    key: "staff.documents.upload",
                    featureFlag: "staffDocumentUpload",
                    requires: ["staff.documents"],
                    icon: "icon.action.upload",
                },
            ],
        },
    ],
};
function recursiveGetPermissions(scopeDef) {
    const scopes = scopeDef.key ? [scopeDef.key] : [];
    if (scopeDef.children) {
        for (const scope of scopeDef.children) {
            scopes.push(...recursiveGetPermissions(scope));
        }
    }
    return scopes;
}
export const ALL_PERMISSIONS = unique(recursiveGetPermissions(PERMISSIONS));
/** The three account ("Konten") permissions. Access to the combined "Konten (Übersicht)"
 * views requires all three. */
export const LEDGER_PERMISSIONS = [
    "manage.employees.ledgers_time",
    "manage.employees.ledgers_vacation",
    "manage.employees.ledgers_bonuses",
];
/** The three account export permissions, split per account type. */
export const LEDGER_EXPORT_PERMISSIONS = [
    "manage.exports.ledgers_time",
    "manage.exports.ledgers_vacation",
    "manage.exports.ledgers_bonuses",
];
export function canEditPermissions(params, profileId) {
    const isOwner = params.role <= Role.Owner;
    const isSelf = profileId != null && params.id === profileId;
    return !isOwner && !isSelf;
}
export function canGrantPermission(permission, grantingProfile, asAdmin = false) {
    if (!grantingProfile || !permission) {
        return false; // No profile to check against
    }
    if (grantingProfile.role <= Role.Owner || asAdmin) {
        return true; // Owners and Admins can grant all permissions
    }
    return grantingProfile.hasPermission(permission);
}
/**
 * Toggles permissions based on user's granting abilities.
 * When checking a permission, only grants sub-permissions the user can grant.
 * When unchecking, only removes sub-permissions the user can grant.
 */
export function togglePermissions({ currentPermissions, checked, grantingProfile, relatedPermissions, asAdmin = false, }) {
    const { sub, requires } = relatedPermissions;
    if (checked) {
        // If we checked a permission, we add all sub permissions that the current user can grant
        const grantableSub = sub.filter((permission) => canGrantPermission(permission, grantingProfile, asAdmin));
        return [...new Set([...currentPermissions, ...grantableSub])];
    }
    else {
        // If we uncheck a permission, we remove only the sub permissions that the current user can grant
        // and all permissions that require this permission
        const removableSub = sub.filter((permission) => canGrantPermission(permission, grantingProfile, asAdmin));
        return currentPermissions.filter((p) => ![...removableSub, ...requires].includes(p));
    }
}
/**
 * Validates a permission change (from `currentPermissions` to `newPermissions`) against the
 * granting profile's own abilities. A manager may only grant/remove permissions they themselves
 * hold; owners and admins bypass all restrictions. Removal is additionally allowed when the profile
 * holds a parent permission of the one being removed
 */
export function assertCanUpdatePermissions(currentPermissions, newPermissions, grantingProfile, asAdmin = false) {
    const permissionsToAdd = newPermissions.filter((permission) => !currentPermissions.includes(permission));
    const permissionsToRemove = currentPermissions.filter((permission) => !newPermissions.includes(permission));
    const unauthorizedAddPermission = permissionsToAdd.find((permission) => !canGrantPermission(permission, grantingProfile, asAdmin));
    if (unauthorizedAddPermission) {
        throw new Err(ErrorCode.INSUFFICIENT_PERMISSIONS, `Sie können die Berechtigung "${unauthorizedAddPermission}" nicht vergeben, da Sie diese selbst nicht besitzen.`);
    }
    const unauthorizedRemovePermission = permissionsToRemove.find((permission) => {
        if (canGrantPermission(permission, grantingProfile, asAdmin)) {
            return false;
        }
        if (!grantingProfile) {
            return true;
        }
        const hasParentPermission = ALL_PERMISSIONS.some((parentPermission) => {
            return permission.startsWith(parentPermission + ".") && grantingProfile.hasPermission(parentPermission);
        });
        return !hasParentPermission;
    });
    if (unauthorizedRemovePermission) {
        throw new Err(ErrorCode.INSUFFICIENT_PERMISSIONS, `Sie können die Berechtigung "${unauthorizedRemovePermission}" nicht entfernen, da Sie diese selbst nicht besitzen.`);
    }
}
//# sourceMappingURL=permissions.js.map